import React, { useEffect } from "react";
import {
  Trash2,
  ShoppingCart,
  Plus,
  Minus,
  ArrowLeft,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

const Cart = () => {
  const { cartItems, removeItem, updateQty } = useCart();
  const navigate = useNavigate();
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  useEffect(() => {
    // Check if user is authenticated
    const customerProfile = localStorage.getItem("customerProfile");
    if (!customerProfile) {
      // If not authenticated, redirect to sign in page
      navigate("/SignIn");
    }
  }, [navigate]);

  // If no customer profile, don't render the cart content
  const customerProfile = localStorage.getItem("customerProfile");
  if (!customerProfile) {
    return null; // or a loading spinner while redirecting
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Your Shopping Cart
          </h1>
          <p className="text-gray-600">Review and manage your selected items</p>
        </div>

        {cartItems.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center border border-gray-100">
            <div className="inline-block p-4 bg-blue-50 rounded-full mb-6">
              <ShoppingCart className="text-blue-600" size={48} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Your cart is empty
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Looks like you haven't added anything to your cart yet. Explore
              our products and find something you love!
            </p>
            <Link to="/Ecwid">
              <button className="px-6 py-3 bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-medium rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-0.5">
                Continue Shopping
              </button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Side - Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-300">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <ShoppingCart className="text-[#82b016]" /> Shopping Cart
                    <span className="bg-[#82b016] text-white text-sm font-normal px-2.5 py-1 rounded-full ml-2">
                      {totalItems} {totalItems === 1 ? "item" : "items"}
                    </span>
                  </h2>
                </div>

                <div className="py-4 h-[600px] overflow-y-scroll">
                  {cartItems.map((item) => (
                    <div key={item.id} className="px-4 py-2">
                      <div className="flex flex-col sm:flex-row bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 transition-all duration-300 p-4">
                        {/* Product Image */}
                        <div className="flex-shrink-0 rounded-xl bg-gray-100 p-2">
                          <img
                            src={item.imageUrl || "/assets/No-img.png.png"}
                            alt={item.name}
                            className="w-24 h-24 object-cover rounded-xl"
                          />
                        </div>

                        {/* Product Details */}
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row justify-between gap-4 px-5">
                            <div>
                              <h3 className="font-bold text-gray-800 text-lg mb-1">
                                {item.name}
                              </h3>
                              <p className="text-sm text-gray-500 mb-2">
                                SKU: {item.sku || "N/A"}
                              </p>
                              <p className="text-lg font-bold text-[#82b016]">
                                ${item.price.toFixed(2)}
                              </p>
                            </div>

                            {/* Quantity and Actions */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                              {/* Quantity Controls */}
                              <div className="flex items-center border border-gray-300 rounded-lg">
                                <button
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-l-lg transition-colors"
                                  onClick={() =>
                                    updateQty(item.id, item.quantity - 1)
                                  }
                                  disabled={item.quantity <= 1}
                                >
                                  <Minus size={16} />
                                </button>
                                <span className="px-3 py-2 text-gray-800 font-medium">
                                  {item.quantity}
                                </span>
                                <button
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-r-lg transition-colors"
                                  onClick={() =>
                                    updateQty(item.id, item.quantity + 1)
                                  }
                                >
                                  <Plus size={16} />
                                </button>
                              </div>

                              {/* Remove Button */}
                              <button
                                onClick={() => removeItem(item.id)}
                                className="flex items-center gap-1 text-red-500 hover:text-red-700 transition-colors p-2 rounded-lg bg-red-100 hover:bg-red-200"
                              >
                                <Trash2 size={18} />
                                <span className="text-sm font-medium">
                                  Remove
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Continue Shopping */}
              <div className="mt-6 flex justify-end">
                <Link to="/Ecwid">
                  <button className="flex items-center bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-bold py-3 px-3 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl">
                    <ArrowLeft size={18} /> Continue Shopping
                  </button>
                </Link>
              </div>
            </div>

            {/* Right Side - Order Summary */}
            <div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-300 sticky top-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6 rounded-md px-3 py-2 bg-gradient-to-r from-gray-100 to-gray-200">
                  Order Summary
                </h3>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({totalItems} items)</span>
                    <span className="font-medium">
                      ${totalAmount.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className="font-medium"></span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span>Tax</span>
                    <span className="font-medium">$0.00</span>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-lg font-bold text-gray-800">
                      <span>Total</span>
                      <span className="text-[#82b016]">
                        ${totalAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <Link
                  to="/checkout"
                  onClick={() =>
                    localStorage.setItem("cart", JSON.stringify(cartItems))
                  }
                >
                  <button className="w-full bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-bold py-3 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl">
                    Proceed To Checkout
                  </button>
                </Link>

                <div className="mt-6 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Secure checkout</span>
                  </div>
                  {/* <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>30-day return policy</span>
                  </div> */}
                </div>
              </div>

              {/* Payment Methods */}
              {/* <div className="mt-6 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h4 className="font-bold text-gray-800 mb-4">We Accept</h4>
                <div className="flex flex-wrap gap-3">
                  <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">Visa</div>
                  <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">MasterCard</div>
                  <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">PayPal</div>
                  <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">Apple Pay</div>
                </div>
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;